import ArticleLink from '@/components/feature/ArticleLink';
import { articlePath, type Article } from '@/utils/content';

export type GlossaryTerm = {
  id: string;
  term: string;
  thai: string;
  group: string;
  definition: string;
  example?: string;
  articleSlug: string;
};

type TermCardProps = {
  term: GlossaryTerm;
  article?: Article;
  related?: GlossaryTerm[];
  onSelectTerm?: (id: string) => void;
  highlighted?: boolean;
};

export default function TermCard({ term, article, related = [], onSelectTerm, highlighted = false }: TermCardProps) {
  return (
    <article
      id={`term-${term.id}`}
      className={`group flex h-full scroll-mt-40 flex-col rounded-xl border bg-background-50 p-5 transition-all duration-300 ${
        highlighted ? 'border-primary-400 ring-2 ring-primary-300' : 'border-background-200 hover:border-primary-300'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-semibold leading-tight text-foreground-950">{term.term}</h3>
          <p className="mt-1 text-sm font-medium text-primary-700">{term.thai}</p>
        </div>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <i className="ri-bookmark-line text-base" />
        </span>
      </div>

      <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{term.definition}</p>

      {term.example && (
        <p className="mt-3 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-xs font-medium leading-relaxed text-accent-800">
          {term.example}
        </p>
      )}

      {related.length > 0 && (
        <div className="mt-4 border-t border-background-200 pt-4">
          <p className="flex items-center gap-1.5 text-xs font-medium text-foreground-500">
            <i className="ri-link text-sm text-primary-500" />
            คำศัพท์ที่เกี่ยวข้อง
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {related.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelectTerm?.(item.id)}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-background-200 bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-400 hover:bg-secondary-50 hover:text-secondary-800"
              >
                <i className="ri-arrow-right-up-line text-xs text-secondary-600" />
                {item.term}
              </button>
            ))}
          </div>
        </div>
      )}

      {article && (
        <ArticleLink
          to={articlePath(article.slug)}
          className="mt-4 flex cursor-pointer items-center gap-1.5 border-t border-background-200 pt-4 text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
        >
          <i className="ri-article-line text-sm" />
          <span className="line-clamp-1">อ่านต่อ: {article.title}</span>
          <i className="ri-arrow-right-line ml-auto text-sm transition-transform group-hover:translate-x-1" />
        </ArticleLink>
      )}
    </article>
  );
}